"use client"

import crypto from "crypto"
import { usePathname } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"

import { CustomPortableText } from "@/components/shared/CustomPortableText"
import { normalizePathForSlugComparison } from "@/lib/redirects"
import { Notification } from "@/types"

import IconX from "./icons/IconX"

interface NotificationBannerProps {
  notification?: Notification | null
  isCompany?: boolean
}

const DEFAULT_EXPIRATION_HOURS = 12
const LG_BREAKPOINT = 1024

export default function NotificationBanner(props: NotificationBannerProps) {
  const notification = props?.isCompany
    ? props?.notification?.company
    : props?.notification?.private
  const pathname = usePathname()
  const [showBanner, setShowBanner] = useState(false)
  const [isScrollHidden, setIsScrollHidden] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)

  const expiration = Number(
    props?.notification?.expiration ?? DEFAULT_EXPIRATION_HOURS
  )
  const normalizedPathname = normalizePathForSlugComparison(pathname)
  const pathnameWithSlash = normalizedPathname ? `/${normalizedPathname}` : "/"
  const pageSpecificNotification = notification?.pageNotifications?.find(
    (n) =>
      n?.page?.slug === normalizedPathname ||
      n?.page?.slug === pathnameWithSlash
  )
  const notificationToUse =
    pageSpecificNotification?.active && pageSpecificNotification?.text
      ? pageSpecificNotification
      : notification
  const notificationContentMD5 = useMemo(() => {
    return crypto
      .createHash("sha256")
      .update(JSON.stringify(notificationToUse?.text ?? ""))
      .digest("hex")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(notificationToUse?.text)])

  // Check if banner should be shown based on localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const closedAt =
        localStorage.getItem("notification-banner-closed") ?? null
      const closedAtMD5 =
        localStorage.getItem("notification-banner-closed-md5") ?? ""
      const contentChanged = closedAtMD5 !== notificationContentMD5
      const expired = !(closedAt
        ? new Date(closedAt) >
          new Date(Date.now() - expiration * 60 * 60 * 1000)
        : false)

      const shouldShowBanner = contentChanged || expired
      setShowBanner(shouldShowBanner)
    }
  }, [expiration, notificationContentMD5])

  // Detect mobile vs desktop
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${LG_BREAKPOINT - 1}px)`)
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches)
    }
    handleChange(mql)
    mql.addEventListener("change", handleChange)
    return () => mql.removeEventListener("change", handleChange)
  }, [])

  // Scroll behavior - hide on scroll down, show on scroll up
  useEffect(() => {
    if (!notificationToUse?.active || !showBanner) {
      setIsScrollHidden(false)
      return
    }

    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY
          const scrollThreshold = 80
          const direction = lastScrollY.current > currentScrollY ? "up" : "down"

          if (currentScrollY > scrollThreshold && direction === "down") {
            setIsScrollHidden(true)
          } else {
            setIsScrollHidden(false)
          }

          lastScrollY.current = currentScrollY
          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [notificationToUse?.active, showBanner])

  useEffect(() => {
    const updateHeight = () => {
      // On mobile, keep the height constant even when scroll-hidden to prevent
      // content jumps (the banner hides via transform, not layout changes).
      // On desktop, zero out the height so the side nav shifts up.
      const isVisible = !isScrollHidden || isMobile
      if (
        bannerRef.current &&
        notificationToUse?.active &&
        showBanner &&
        isVisible
      ) {
        const height = bannerRef.current.offsetHeight
        document.documentElement.style.setProperty(
          "--notification-banner-height",
          `${height}px`
        )
      } else {
        document.documentElement.style.setProperty(
          "--notification-banner-height",
          "0px"
        )
      }
    }

    updateHeight()

    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(updateHeight, 100)
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [
    notificationToUse?.active,
    notificationToUse?.text,
    showBanner,
    isScrollHidden,
    isMobile
  ])

  const handleClose = () => {
    const now = new Date().toISOString()
    localStorage.setItem("notification-banner-closed", now)
    localStorage.setItem(
      "notification-banner-closed-md5",
      notificationContentMD5
    )
    setShowBanner(false)
  }

  return !notificationToUse?.active ||
    !notificationToUse?.text ||
    !showBanner ? null : (
    <div
      ref={bannerRef}
      className={`
        p-4 z-notificationBanner fixed top-0 left-0 right-0 w-full
        bg-yellow-50
        transition-transform duration-300
        ${isScrollHidden ? "-translate-y-full" : "translate-y-0"}
      `}
      style={{
        transitionTimingFunction: "cubic-bezier(0.3, 1, 0.33, 1)"
      }}>
      <div className="text-sm pr-6">
        {notificationToUse?.text && (
          <CustomPortableText
            value={notificationToUse?.text}
            paragraphClasses="text-sm text-black"
          />
        )}
      </div>
      <button
        className="text-sm text-black absolute top-4 right-4 hover:text-gray-700 transition-colors"
        onClick={handleClose}
        aria-label="Stäng meddelande">
        <IconX className="w-4 h-4" />
      </button>
    </div>
  )
}
